// src/hooks/sync/usePrayerAcks.js
// "I prayed for you" notices between app users who are prayer partners.
//
//   usePrayerAcks()  → receiver side. Mount once in Layout. Listens to
//                      prayerAcks where toUid == me, shows a toast (and a local
//                      notification when the app is in the background), then
//                      marks each notice read. Notices older than 7 days are deleted.
//   usePrayerAck()   → sender side. Returns notifyPrayed(request): writes ONE
//                      notice per sender → partner per day. A second tap the same
//                      day is rejected by the rules and ignored here.
//
// Spark plan: no server push. A partner whose app is fully closed sees the
// notice the next time they open the app.
import { useCallback, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  collection, deleteDoc, doc, limit, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where,
} from 'firebase/firestore';
import { toast } from 'sonner';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import { STORE_USER_PROFILE, QUERY_LIMIT } from '@/lib/storeKeys';
import { formatDate } from '@/utils/helpers';

const NOTIFICATION_ID = 7311;
const CHANNEL_ID      = 'prayer-acks';
const KEEP_DAYS       = 7;

async function notifyInBackground(body) {
  if (!Capacitor.isNativePlatform() || document.visibilityState === 'visible') return;
  try {
    const { LocalNotifications: LN } = await import('@capacitor/local-notifications');
    const perm = await LN.checkPermissions();
    if (perm.display !== 'granted') return; // never prompt from here
    try {
      await LN.createChannel({
        id: CHANNEL_ID, name: 'Prayer partners',
        description: 'When a prayer partner prays for you', importance: 3,
      });
    } catch { /* createChannel is Android-only */ }
    await LN.schedule({
      notifications: [{
        id: NOTIFICATION_ID,
        title: 'Prayer 🙏',
        body,
        channelId: CHANNEL_ID,
        schedule: { at: new Date(Date.now() + 500), allowWhileIdle: true },
      }],
    });
  } catch (err) {
    console.warn('[usePrayerAcks] local notification failed:', err?.message || err);
  }
}

// ── Receiver ────────────────────────────────────────────────────────────────
export function usePrayerAcks() {
  const { user } = useAuth();
  const uid = user?.uid && user.uid !== 'offline' ? user.uid : null;
  const handledRef = useRef(new Set());

  useEffect(() => {
    if (!uid || !db) return undefined;
    const q = query(collection(db, 'prayerAcks'), where('toUid', '==', uid), limit(QUERY_LIMIT));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const cutoff = formatDate(new Date(Date.now() - KEEP_DAYS * 86400000));
        const fresh = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          if (String(data.day || '') < cutoff) {
            deleteDoc(d.ref).catch(() => {});
          } else if (data.read !== true && !handledRef.current.has(d.id)) {
            handledRef.current.add(d.id);
            fresh.push(d);
          }
        });
        if (fresh.length === 0) return;

        const names = [...new Set(fresh.map((d) => String(d.data().fromName || 'A friend')))];
        const who = names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3} more` : '');
        toast(`${who} prayed for you today 🙏`, { duration: 8000 });
        notifyInBackground(`${who} prayed for you today`);
        fresh.forEach((d) =>
          updateDoc(d.ref, { read: true }).catch((err) =>
            console.warn('[usePrayerAcks] could not mark read:', err?.code || err)),
        );
      },
      (err) => console.warn('[usePrayerAcks] listener error:', err?.code || err),
    );
    return unsub;
  }, [uid]);
}

// ── Sender ──────────────────────────────────────────────────────────────────
export function usePrayerAck() {
  const { user } = useAuth();
  const uid = user?.uid && user.uid !== 'offline' ? user.uid : null;
  const [profile] = useSyncStore(STORE_USER_PROFILE, {});
  const myName = String(profile?.displayName || user?.displayName || '').trim().slice(0, 60);

  return useCallback((req) => {
    const toUid = req?.partnerUid;
    if (!uid || !db || !toUid || toUid === uid) return;
    const day = formatDate(new Date());
    setDoc(doc(db, 'prayerAcks', `${uid}_${toUid}_${day}`), {
      toUid,
      fromUid:   uid,
      fromName:  myName || 'A friend',
      day,
      createdAt: serverTimestamp(),
      read:      false,
    })
      .then(() => toast.success(`${req.partnerName || 'Your prayer partner'} will see that you prayed`))
      .catch((err) => {
        // permission-denied = already sent today, or the partner removed the link. Both are fine.
        if (err?.code !== 'permission-denied') console.warn('[usePrayerAck] send failed:', err?.code || err);
      });
  }, [uid, myName]);
}

export default usePrayerAcks;